/**
 * ContractForm — Create/edit contract form, en verlenging vanuit een
 * contracteinde-beslispunt (prop `verlenging`): vooringevuld uit het oude
 * contract, start = oud einde + 1 en proeftijd 0 liggen vast, contractduur
 * vergrendeld op onbepaald als de ketenregeling dat eist. Opslaan loopt dan
 * via POST /api/workforce/contract-einde (keuze verlengen_nieuw), zodat
 * stempel en aanzegmail meelopen.
 *
 * Pulls employee data (name, address, birthdate) from staff record automatically.
 * Calculates CAO uurloon based on loongroep, geboortedatum, and employment type.
 */

import { useState, useMemo } from 'react';
import type { Staff, Contract, ContractCreateData } from '../../stores/workforce-store';
import { getCaoUurloon } from '../../lib/cao-salary';

// Wettelijk maximum proeftijd (art. 7:652 BW): onbepaalde tijd max 2 maanden;
// bepaalde tijd van 6 maanden of korter geen proeftijd, korter dan 2 jaar max 1 maand, vanaf 2 jaar max 2 maanden.
function maxProeftijdMaanden(contractType: 'bepaald' | 'onbepaald', startDate: string, endDate: string): number {
  if (contractType === 'onbepaald' || !startDate || !endDate) return 2;
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  const plus6m = new Date(start);
  plus6m.setMonth(plus6m.getMonth() + 6);
  const plus2j = new Date(start);
  plus2j.setFullYear(plus2j.getFullYear() + 2);
  if (end < plus6m) return 0;
  if (end < plus2j) return 1;
  return 2;
}

export interface ContractVerlenging {
  basis: Contract;
  voorstel: { start_date: string; end_date: string | null; contract_type: 'bepaald' | 'onbepaald' };
  onbepaaldVerplicht: boolean;
  ketenReden?: string | null;
}

interface ContractFormProps {
  staff: Staff;
  contract?: Contract;
  verlenging?: ContractVerlenging;
  onSave: (data: ContractCreateData) => Promise<void>;
  onCancel: () => void;
}

export function ContractForm({ staff, contract: bewerkContract, verlenging, onSave, onCancel }: ContractFormProps) {
  // Bij verlenging is het oude contract de bron voor alle voorwaarden
  const contract = bewerkContract ?? verlenging?.basis;
  const isVerlenging = !!verlenging;
  const nameParts = staff.name.trim().split(/\s+/);
  const defaultAchternaam = nameParts.length > 1 ? nameParts.slice(1).join(' ') : staff.name;

  const defaultEmploymentType = staff.contract_type === 'min-max'
    ? 'min-max'
    : staff.contract_type === 'jaaruren'
    ? 'jaaruren'
    : (staff.contract_type === '0-uren' || staff.contract_type === 'student' ? '0-uren-allin' : 'regulier');

  const [form, setForm] = useState({
    contract_type: (verlenging ? verlenging.voorstel.contract_type : contract?.contract_type || 'bepaald') as 'bepaald' | 'onbepaald',
    employment_type: (contract?.employment_type || defaultEmploymentType) as 'regulier' | '0-uren-allin' | '0-uren-opbouw' | 'min-max' | 'jaaruren' | 'vast-salaris',
    functie: contract?.functie || staff.functie || 'Medewerker specialistische reiniging',
    // staff.end_date is de uitdienstdatum, geen contracteinde: niet als einddatum voorinvullen
    start_date: verlenging ? verlenging.voorstel.start_date : contract?.start_date || staff.start_date || '',
    end_date: verlenging ? verlenging.voorstel.end_date || '' : contract?.end_date || '',
    oorspronkelijke_datum_in_dienst: contract?.oorspronkelijke_datum_in_dienst || '',
    proeftijd_maanden: verlenging ? '0' : contract?.proeftijd_maanden?.toString() || '1',
    uren_per_week: contract?.uren_per_week?.toString() || staff.uren_per_week?.toString() || '',
    uren_min: contract?.uren_min?.toString() || '20',
    uren_max: contract?.uren_max?.toString() || '40',
    maandsalaris: contract?.bruto_maandsalaris?.toString() || '',
    // Vast-salaris: vakantie in dagen i.p.v. percentage (dag = uren_per_week / 5)
    vakantiedagen: contract?.vakantieuren_per_jaar && contract?.uren_per_week
      ? String(Math.round(contract.vakantieuren_per_jaar / (contract.uren_per_week / 5)))
      : '25',
    // Prefill uit staff.cao_ervaringsjaren als het contractveld leeg is
    dienstjaren_ervaring: contract?.cao_dienstjaren?.toString() ?? staff.cao_ervaringsjaren?.toString() ?? '',
    loongroep: (contract?.loongroep ?? staff.loongroep)?.toString() || '1',
    uurloon_override: contract?.bruto_uurloon?.toString() || '',
    use_cao: !contract, // New contracts default to CAO, edits keep manual
    vakantie_percentage: contract?.vakantie_percentage?.toString() || '10',
    vakantietoeslag_percentage: contract?.vakantietoeslag_percentage?.toString() || '8',
    bedrijf_vertegenwoordigd_door: contract?.bedrijf_vertegenwoordigd_door || 'A.J. Blok',
    notes: contract?.notes || '',
  });
  const isEditing = !!bewerkContract;
  const [isSaving, setIsSaving] = useState(false);

  const isRegular = form.employment_type === 'regulier';
  const isBepaald = form.contract_type === 'bepaald';
  const isAllIn = form.employment_type === '0-uren-allin';
  const isMinMax = form.employment_type === 'min-max';
  const isJaaruren = form.employment_type === 'jaaruren';
  // Vast salaris: bruto maandsalaris zonder urenregistratie; uurloon is de afgeleide waarde
  const isVastSalaris = form.employment_type === 'vast-salaris';
  // Vaste-uren-vormen delen uren_per_week + vakantieberekening (op percentage)
  const hasVasteUren = isRegular || isJaaruren;

  // Contractwissel: zittende werknemer krijgt een vervangend contract met behoud van anciënniteit
  const isWissel = !!form.oorspronkelijke_datum_in_dienst && form.oorspronkelijke_datum_in_dienst !== form.start_date;

  // Proeftijd geklemd op het wettelijk maximum voor deze contractduur;
  // bij voortzetting van een bestaand dienstverband (zelfde werk) is een proeftijd nietig,
  // ook in een opvolgend contract (art. 7:652 lid 8)
  const maxProeftijd = isWissel || isVerlenging ? 0 : maxProeftijdMaanden(form.contract_type, form.start_date, isBepaald ? form.end_date : '');
  const effectiveProeftijd = Math.min(parseInt(form.proeftijd_maanden, 10) || 0, maxProeftijd);

  // Calculate CAO uurloon
  const dienstjarenOverride = form.dienstjaren_ervaring.trim() !== '' && !Number.isNaN(parseInt(form.dienstjaren_ervaring, 10))
    ? parseInt(form.dienstjaren_ervaring, 10)
    : null;
  const caoResult = useMemo(() => {
    if (!form.loongroep || !staff.geboortedatum) return null;
    return getCaoUurloon(
      parseInt(form.loongroep, 10),
      staff.start_date || form.start_date || '2026-01-01',
      staff.geboortedatum,
      isAllIn ? 'all-in' : 'bruto',
      undefined,
      dienstjarenOverride,
    );
  }, [form.loongroep, form.start_date, staff.geboortedatum, staff.start_date, isAllIn, dienstjarenOverride]);

  const maandsalarisNum = parseFloat(form.maandsalaris) || 0;
  const urenNum = parseFloat(form.uren_per_week) || 0;
  // Vast-salaris: uurloon afgeleid uit het maandsalaris (compatibiliteit met lezers van bruto_uurloon)
  const effectiveUurloon = isVastSalaris
    ? (urenNum > 0 ? Number((maandsalarisNum * 12 / (52 * urenNum)).toFixed(2)) : 0)
    : form.use_cao && caoResult ? caoResult.uurloon : parseFloat(form.uurloon_override) || 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const uren = form.uren_per_week ? parseFloat(form.uren_per_week) : null;
      const pct = parseFloat(form.vakantie_percentage);
      // Vast-salaris: vakantie op dagen (dag = uren_per_week / 5)
      const vakantiedagen = parseInt(form.vakantiedagen, 10) || 0;
      const vsVakantieuren = isVastSalaris && uren ? Number((vakantiedagen * uren / 5).toFixed(1)) : null;

      await onSave({
        staff_id: staff.id,
        contract_type: form.contract_type,
        employment_type: form.employment_type,
        functie: form.functie,
        start_date: form.start_date,
        end_date: isBepaald ? form.end_date : null,
        oorspronkelijke_datum_in_dienst: isVerlenging
          ? (verlenging!.basis.oorspronkelijke_datum_in_dienst || verlenging!.basis.start_date)
          : isWissel ? form.oorspronkelijke_datum_in_dienst : null,
        proeftijd_maanden: effectiveProeftijd,
        uren_per_week: (hasVasteUren || isVastSalaris) ? uren : null,
        uren_min: isMinMax && form.uren_min ? parseFloat(form.uren_min) : null,
        uren_max: isMinMax && form.uren_max ? parseFloat(form.uren_max) : null,
        jaaruren_norm: isJaaruren && uren ? Number((uren * 52).toFixed(0)) : null,
        cao_dienstjaren: isVastSalaris ? null : dienstjarenOverride,
        // Op het contract én (via API/cron loonkaart-sync) op de loonkaart: anders rekent de CAO-sync
        // met staff.loongroep (default 1) en zet hij het contractloon terug (Hans de Vries, 10-09-2026)
        loongroep: isVastSalaris ? null : parseInt(form.loongroep, 10) || null,
        uurloon_via_cao: !isVastSalaris && form.use_cao,
        bruto_uurloon: effectiveUurloon,
        bruto_maandsalaris: isVastSalaris ? maandsalarisNum : null,
        vakantie_percentage: pct,
        vakantietoeslag_percentage: parseFloat(form.vakantietoeslag_percentage),
        vakantie_opbouw_per_week: isVastSalaris
          ? (vsVakantieuren ? Number((vsVakantieuren / 52).toFixed(2)) : null)
          : hasVasteUren && uren ? Number((uren * pct / 100).toFixed(2)) : null,
        vakantieuren_per_jaar: isVastSalaris
          ? vsVakantieuren
          : hasVasteUren && uren ? Number((uren * 52 * pct / 100).toFixed(1)) : null,
        // Snapshot from staff record — no manual entry needed
        aanhef_werknemer: staff.aanhef || '',
        voorletters: staff.voorletters || '',
        achternaam: contract?.achternaam || defaultAchternaam,
        geboortedatum: staff.geboortedatum || '',
        woonplaats: staff.woonplaats || staff.stad || '',
        bedrijf_vertegenwoordigd_door: form.bedrijf_vertegenwoordigd_door,
        datum_opgemaakt: new Date().toISOString().split('T')[0]!,
        previous_contract_id: isVerlenging ? verlenging!.basis.id : null,
        notes: form.notes || null,
        created_by: null,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none';
  const labelClass = 'block text-xs font-medium text-slate-500 mb-1';
  const sectionClass = 'text-xs font-semibold text-slate-400 uppercase tracking-wider';

  // Check if staff data is incomplete for contract
  const missingFields = [];
  if (!staff.aanhef) missingFields.push('aanhef');
  if (!staff.voorletters) missingFields.push('voorletters');
  if (!staff.geboortedatum) missingFields.push('geboortedatum');
  if (!staff.woonplaats && !staff.stad) missingFields.push('woonplaats');

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <h2 className="text-lg font-semibold text-slate-900">
        {isVerlenging ? `Verlengen met nieuwe voorwaarden: ${verlenging!.basis.contract_number}` : isEditing ? 'Contract bewerken' : 'Nieuw contract'}
      </h2>

      {isVerlenging && (
        <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-800">
          Vooringevuld met de voorwaarden van {verlenging!.basis.contract_number}; pas aan wat verandert.
          Ingangsdatum en proeftijd liggen vast (opvolgend contract, geen proeftijd).
          Opslaan maakt het concept, legt het besluit vast en stuurt de aanzegmail met de wijzigingen.
          {verlenging!.onbepaaldVerplicht && (
            <span className="block mt-1 font-medium">Ketenregeling: {verlenging!.ketenReden ?? 'het volgende contract is van rechtswege voor onbepaalde tijd'}.</span>
          )}
        </div>
      )}

      {/* Warning if staff data incomplete */}
      {missingFields.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          Let op: ontbrekende gegevens bij medewerker ({missingFields.join(', ')}). Vul deze eerst aan bij &ldquo;Bewerken&rdquo;.
        </div>
      )}

      {/* Staff snapshot (read-only, pulled from staff record) */}
      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Werknemer</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-sm">
          <div><span className="text-slate-500">Naam:</span> <span className="font-medium text-slate-800">{staff.aanhef ? `${staff.aanhef} ` : ''}{staff.voorletters ? `${staff.voorletters} ` : ''}{defaultAchternaam}</span></div>
          <div><span className="text-slate-500">Geboortedatum:</span> <span className="font-medium text-slate-800">{staff.geboortedatum || '—'}</span></div>
          <div><span className="text-slate-500">Woonplaats:</span> <span className="font-medium text-slate-800">{staff.woonplaats || staff.stad || '—'}</span></div>
          <div><span className="text-slate-500">Team:</span> <span className="font-medium text-slate-800 capitalize">{staff.team || '—'}</span></div>
        </div>
      </div>

      {/* Contract type */}
      <section className="space-y-4">
        <h3 className={sectionClass}>Type</h3>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-3">
            <label className={labelClass}>Contractduur *</label>
            <select required disabled={verlenging?.onbepaaldVerplicht} value={form.contract_type} onChange={e => setForm(f => ({ ...f, contract_type: e.target.value as 'bepaald' | 'onbepaald' }))} className={inputClass}>
              <option value="bepaald" disabled={verlenging?.onbepaaldVerplicht}>Bepaalde tijd</option>
              <option value="onbepaald">Onbepaalde tijd</option>
            </select>
          </div>
          <div className="col-span-3">
            <label className={labelClass}>Type dienstverband *</label>
            <select required value={form.employment_type} onChange={e => setForm(f => ({ ...f, employment_type: e.target.value as 'regulier' | '0-uren-allin' | '0-uren-opbouw' | 'min-max' | 'jaaruren' | 'vast-salaris' }))} className={inputClass}>
              <option value="regulier">Regulier (vaste uren)</option>
              <option value="jaaruren">Jaaruren (urennorm per jaar)</option>
              <option value="vast-salaris">Vast salaris (geen urenregistratie)</option>
              <option value="min-max">Min/max (garantie-uren)</option>
              <option value="0-uren-allin">0-uren (all-in)</option>
              <option value="0-uren-opbouw">0-uren (met opbouw)</option>
            </select>
          </div>
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* Contract details */}
      <section className="space-y-4">
        <h3 className={sectionClass}>Details</h3>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-6 sm:col-span-3">
            <label className={labelClass}>Functie *</label>
            <input type="text" required value={form.functie} onChange={e => setForm(f => ({ ...f, functie: e.target.value }))} className={inputClass} />
          </div>
          <div className="col-span-3 sm:col-span-3">
            <label className={labelClass}>Proeftijd</label>
            <select disabled={isVerlenging} value={String(effectiveProeftijd)} onChange={e => setForm(f => ({ ...f, proeftijd_maanden: e.target.value }))} className={inputClass}>
              <option value="0">Geen proeftijd</option>
              <option value="1" disabled={maxProeftijd < 1}>1 maand</option>
              <option value="2" disabled={maxProeftijd < 2}>2 maanden</option>
            </select>
            {maxProeftijd < 2 && (
              <p className="mt-1 text-[10px] text-slate-400">
                {isWissel || isVerlenging
                  ? 'Bij voortzetting van een bestaand dienstverband is een proeftijd nietig.'
                  : maxProeftijd === 0
                  ? 'Bij bepaalde tijd van 6 maanden of korter is een proeftijd wettelijk nietig.'
                  : 'Bij bepaalde tijd korter dan 2 jaar is de proeftijd wettelijk maximaal 1 maand.'}
              </p>
            )}
          </div>
          <div className="col-span-3">
            <label className={labelClass}>Begindatum *</label>
            <input type="date" required disabled={isVerlenging} value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className={inputClass} />
          </div>
          {isBepaald && (
            <div className="col-span-3">
              <label className={labelClass}>Einddatum *</label>
              <input type="date" required value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className={inputClass} />
            </div>
          )}
          <div className="col-span-3">
            <label className={labelClass}>Oorspronkelijk in dienst sinds</label>
            <input type="date" value={form.oorspronkelijke_datum_in_dienst} onChange={e => setForm(f => ({ ...f, oorspronkelijke_datum_in_dienst: e.target.value }))} className={inputClass} />
            <p className="mt-1 text-[10px] text-slate-400">
              Alleen bij een contractwissel van een zittende werknemer. Artikel 1 wordt dan een vervangingsclausule met behoud van anciënniteit; leeg = nieuwe indiensttreding.
              {staff.start_date && staff.start_date !== form.start_date ? ` Medewerker staat sinds ${staff.start_date} in het systeem.` : ''}
            </p>
          </div>
          {(isRegular || isJaaruren || isVastSalaris) && (
            <div className="col-span-3">
              <label className={labelClass}>{(isJaaruren || isVastSalaris) ? 'Uren per week (gemiddeld) *' : 'Uren per week *'}</label>
              <input type="number" required step="0.5" value={form.uren_per_week} onChange={e => setForm(f => ({ ...f, uren_per_week: e.target.value }))} className={inputClass} placeholder={isVastSalaris ? '38' : '40'} />
              {isVastSalaris && (
                <p className="mt-1 text-[10px] text-slate-400">Alleen als basis voor vakantie en ziekte — er wordt niet geklokt.</p>
              )}
            </div>
          )}
          {isMinMax && (
            <>
              <div className="col-span-3 sm:col-span-2">
                <label className={labelClass}>Min. uren per week *</label>
                <input type="number" required step="0.5" min="0" value={form.uren_min} onChange={e => setForm(f => ({ ...f, uren_min: e.target.value }))} className={inputClass} placeholder="20" />
              </div>
              <div className="col-span-3 sm:col-span-2">
                <label className={labelClass}>Max. uren per week *</label>
                <input type="number" required step="0.5" min="0" value={form.uren_max} onChange={e => setForm(f => ({ ...f, uren_max: e.target.value }))} className={inputClass} placeholder="40" />
              </div>
            </>
          )}
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* Salary */}
      <section className="space-y-4">
        <h3 className={sectionClass}>Loon</h3>
        {isVastSalaris ? (
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-3 sm:col-span-2">
              <label className={labelClass}>Bruto maandsalaris *</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-sm text-slate-400">&euro;</span>
                <input type="number" required step="0.01" min="0.01" value={form.maandsalaris} onChange={e => setForm(f => ({ ...f, maandsalaris: e.target.value }))} className={`${inputClass} pl-7`} placeholder="5500" />
              </div>
            </div>
            <div className="col-span-3 sm:col-span-2">
              <label className={labelClass}>Vakantiedagen per jaar *</label>
              <input type="number" required step="1" min="20" value={form.vakantiedagen} onChange={e => setForm(f => ({ ...f, vakantiedagen: e.target.value }))} className={inputClass} placeholder="25" />
              <p className="mt-1 text-[10px] text-slate-400">Wettelijk minimum 20 bij voltijd &middot; reguliere contracten hebben er 26</p>
            </div>
            <div className="col-span-3 sm:col-span-2">
              <label className={labelClass}>Vakantietoeslag %</label>
              <input type="number" step="0.1" value={form.vakantietoeslag_percentage} onChange={e => setForm(f => ({ ...f, vakantietoeslag_percentage: e.target.value }))} className={inputClass} />
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Loongroep (CAO)</label>
            <select value={form.loongroep} onChange={e => setForm(f => ({ ...f, loongroep: e.target.value }))} className={inputClass}>
              {[1, 2, 3, 4, 5, 6].map(lg => (
                <option key={lg} value={lg}>Loongroep {lg}</option>
              ))}
            </select>
          </div>
          {!isAllIn && (
            <div className="col-span-3 sm:col-span-2">
              <label className={labelClass}>Dienstjaren ervaring (CAO)</label>
              <input
                type="number" min="0" step="1"
                value={form.dienstjaren_ervaring}
                onChange={e => setForm(f => ({ ...f, dienstjaren_ervaring: e.target.value }))}
                className={inputClass}
                placeholder="Automatisch"
              />
              <p className="mt-1 text-[10px] text-slate-400">Leeg = o.b.v. datum in dienst &middot; 0 = trede 1, 1 = trede 2, 2 = trede 3, 3+ = trede 4</p>
            </div>
          )}
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>{isAllIn ? 'All-in uurloon' : 'Bruto uurloon'} *</label>
            {form.use_cao && caoResult ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm font-medium text-emerald-800">
                  &euro; {caoResult.uurloon.toFixed(2)}
                </div>
                <button type="button" onClick={() => setForm(f => ({ ...f, use_cao: false, uurloon_override: caoResult.uurloon.toFixed(2) }))} className="text-[10px] text-slate-400 hover:text-slate-600 whitespace-nowrap">
                  Handmatig
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-2 text-sm text-slate-400">&euro;</span>
                  <input type="number" required step="0.01" min="0.01" value={form.uurloon_override} onChange={e => setForm(f => ({ ...f, uurloon_override: e.target.value }))} className={`${inputClass} pl-7`} placeholder="14.50" />
                </div>
                {caoResult && (
                  <button type="button" onClick={() => setForm(f => ({ ...f, use_cao: true }))} className="text-[10px] text-indigo-500 hover:text-indigo-700 whitespace-nowrap">
                    CAO
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="col-span-6 sm:col-span-2">
            {caoResult && form.use_cao && (
              <div className="mt-5 text-xs text-slate-500">
                {caoResult.isAllIn ? (
                  <span>All-in (incl. vakantie + toeslag) &middot; {caoResult.leeftijd < 20 ? `${caoResult.leeftijd} jaar` : '20+'}</span>
                ) : caoResult.isYouth ? (
                  <span>Jeugdloon &middot; {caoResult.leeftijd} jaar</span>
                ) : (
                  <span>Trede {caoResult.trede} &middot; {caoResult.dienstjaren} dienstjaren</span>
                )}
              </div>
            )}
          </div>

          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Vakantie %</label>
            <input type="number" step="0.1" value={form.vakantie_percentage} onChange={e => setForm(f => ({ ...f, vakantie_percentage: e.target.value }))} className={inputClass} />
          </div>
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Vakantietoeslag %</label>
            <input type="number" step="0.1" value={form.vakantietoeslag_percentage} onChange={e => setForm(f => ({ ...f, vakantietoeslag_percentage: e.target.value }))} className={inputClass} />
          </div>
        </div>
        )}

        {/* Vast-salaris: afgeleide waarden */}
        {isVastSalaris && urenNum > 0 && maandsalarisNum > 0 && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
            Afgeleid uurloon &euro; {effectiveUurloon.toFixed(2)} (voor systemen die op uurloon lezen)
            &middot; {((parseInt(form.vakantiedagen, 10) || 0) * urenNum / 5).toFixed(1)} vakantie-uren/jaar
            &middot; jaarsalaris &euro; {(maandsalarisNum * 12).toFixed(0)} excl. vakantietoeslag
          </div>
        )}

        {/* Calculated vakantie-uren */}
        {hasVasteUren && form.uren_per_week && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
            {isJaaruren && (
              <>
                Jaarurennorm: {Math.round(parseFloat(form.uren_per_week) * 52)} uur per kalenderjaar
                {effectiveUurloon > 0 && (
                  <> &middot; vast maandsalaris &euro; {(parseFloat(form.uren_per_week) * 52 * effectiveUurloon / 12).toFixed(2)}</>
                )}
                <br />
              </>
            )}
            Berekend: {(parseFloat(form.uren_per_week) * parseFloat(form.vakantie_percentage) / 100).toFixed(2)} vakantie-uren/week
            &middot; {(parseFloat(form.uren_per_week) * 52 * parseFloat(form.vakantie_percentage) / 100).toFixed(1)} vakantie-uren/jaar
          </div>
        )}
      </section>

      <hr className="border-gray-100" />

      {/* Werkgever */}
      <section className="space-y-4">
        <h3 className={sectionClass}>Werkgever</h3>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-6 sm:col-span-3">
            <label className={labelClass}>Vertegenwoordigd door</label>
            <input type="text" value={form.bedrijf_vertegenwoordigd_door} onChange={e => setForm(f => ({ ...f, bedrijf_vertegenwoordigd_door: e.target.value }))} className={inputClass} />
          </div>
        </div>
      </section>

      {/* Notes */}
      <div>
        <label className={labelClass}>Notities (intern)</label>
        <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className={inputClass} rows={2} />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
        <button type="submit" disabled={isSaving || !form.start_date || effectiveUurloon <= 0} className="px-6 py-2.5 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 disabled:opacity-50 transition-colors">
          {isSaving ? 'Opslaan...' : isEditing ? 'Opslaan' : isVerlenging ? 'Verlengen en aanzeggen' : 'Contract aanmaken'}
        </button>
        <button type="button" onClick={onCancel} className="px-6 py-2.5 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium transition-colors">
          Annuleren
        </button>
      </div>
    </form>
  );
}
